<template>
    <div class="custom-field stretch">

        <ErrorField class="column first" :errors="propertyNameErrors">
            <input
                class="property-name"
                type="text"
                :value="customName"
                placeholder="Property Name"
                :maxlength="maxLength"
                @input="$emit('update:customName', $event.target.value)"
            />
        </ErrorField>

        <ErrorField class="column" :condition="errors.type" field="Data Type" type="required">
            <Dropdown
                :selectedId="customType"
                :options="dataTypeOptions"
                defaultText="Select Data Type"
                :searchEnabled="false"
                @dropdownSelectedItemChanged="$emit('update:customType', $event.id)"
                height="34"
            />
        </ErrorField>

        <IntegerField
            v-if="customType === segmentConsts.FIELD_TYPES.Integer"
            v-bind="commonFieldProps"
            v-on="commonFieldListeners"
        />

        <FloatField
            v-if="customType === segmentConsts.FIELD_TYPES.Float"
            v-bind="commonFieldProps"
            v-on="commonFieldListeners"
        />

        <StringField
            v-if="customType === segmentConsts.FIELD_TYPES.String"
            v-bind="commonFieldProps"
            v-on="commonFieldListeners"
        />

        <BooleanField
            v-if="customType === segmentConsts.FIELD_TYPES.Boolean"
            :value="value"
            :errors="errors"
            @update:value="updateBooleanField"
        />

        <DateField
            v-if="customType === segmentConsts.FIELD_TYPES.Date"
            v-bind="commonFieldProps"
            v-on="commonFieldListeners"
            :inputWidth="120"
        />

    </div>
</template>

<script>
import Dropdown from 'components/dropdowns/Dropdown.vue';
import ErrorField from 'components/errorField/ErrorField.vue';
import IntegerField from './IntegerField.vue';
import StringField from './StringField.vue';
import BooleanField from './BooleanField.vue';
import FloatField from './FloatField.vue';
import DateField from './DateField.vue';
import * as segmentConsts from '../consts';
import { customNameMaxLength } from '../consts';

export default {
    name: 'CustomField',
    components: {
        ErrorField,
        DateField,
        FloatField,
        BooleanField,
        Dropdown,
        IntegerField,
        StringField
    },
    props: {
        value: [String, Number, Boolean],
        errors: Object,
        comparator: String,
        customType: String,
        customName: String,
        metadata: Object,
        maxLength: {
            type: Number,
            default: 32
        }
    },
    computed: {
        propertyNameErrors() {
            return [
                {
                    condition: this.errors.customName,
                    field: 'Property Name',
                    type: 'required'
                },
                {
                    condition: this.errors.customNameMaxLength,
                    field: 'Property Name',
                    value: customNameMaxLength,
                    type: 'maxLength'
                }
            ];
        },
        dataTypesMeta() {
            return this.metadata.supportedDataTypes || {};
        },
        dataTypeOptions() {
            return Object.keys(this.dataTypesMeta)
                .map((type) => ({
                    id: type,
                    displayText: type
                }));
        },
        comparatorOptions() {
            const typeMeta = this.dataTypesMeta[this.customType] || {};
            return this.makeOptionsFromArray(typeMeta.comparators || []);
        },
        commonFieldProps() {
            return {
                value: this.value,
                comparator: this.comparator,
                comparatorOptions: this.comparatorOptions,
                placeholder: this.customType,
                errors: this.errors,
                maxLength: this.maxLength
            };
        },
        commonFieldListeners() {
            return {
                'update:comparator': this.handleComparatorChange,
                'update:value': this.handleValueChange
            };
        },
        segmentConsts: () => segmentConsts
    },
    methods: {
        makeOptionsFromArray(arr) {
            return arr ? arr.map((el) => ({
                id: el,
                displayText: el
            })) : [];
        },
        updateBooleanField(value) {
            this.handleValueChange(value);
            this.handleComparatorChange('Equals');
        },
        handleValueChange(val) {
            this.$emit('update:value', val);
        },
        handleComparatorChange(val) {
            this.$emit('update:comparator', val);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";
@import "~styles/input.scss";

.property-name {
    width: 100%;
}

.custom-field {
    // some of the hepper styles that are defined in
    // SegmentCondition component are overridden below
    // to adjust it for custom field context
    /deep/ {
        .column {
            width: 150px;
            &.first {
                padding-top: 0;
                padding-left: 10px;
                width: 200px;
            }
        }
        .stretch {
            display: flex;
            flex: 1;
            align-items: flex-start;
            width: auto;
        }
    }
}

</style>
